import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/homepage/Navbar";
import HeroSection from "@/components/homepage/HeroSection";
import ServicesSection from "@/components/homepage/ServicesSection";
import WhyChooseUsSection from "@/components/homepage/WhyChooseUsSection";
import AITasteBuilderSection from "@/components/AITasteBuilderSection";
import TeamSection from "@/components/TeamSection";
import ContactSection from "@/components/homepage/ContactSection";
import Footer from "@/components/homepage/Footer";
import FloatingCTA from "@/components/FloatingCTA";

interface HomepageSettings {
  show_team_section: boolean;
}

const Index = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();
  const isReturningToEdit = location.state?.returnToStep1 === true;

  useEffect(() => {
    // Don't clear data if user is returning to edit contact info
    if (isReturningToEdit) {
      // Scroll to contact section
      setTimeout(() => {
        const el = document.getElementById('contact-section');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
      return;
    }

    // Clear previous inquiry data when landing on homepage (unless returning mid-flow)
    const existingSessionId = sessionStorage.getItem('session_id');
    const isReturningFromFlow = sessionStorage.getItem('inquiry_in_progress');
    
    if (existingSessionId && !isReturningFromFlow) {
      sessionStorage.removeItem('inquiry_in_progress');
      
      supabase
        .from('favorite_songs')
        .delete()
        .eq('session_id', existingSessionId)
        .then(({ error }) => {
          if (error) {
            console.error('Error clearing old songs:', error);
          } else {
            console.log('Cleared old session songs for fresh inquiry');
          }
        });
      
      supabase
        .from('contact_form_drafts')
        .delete()
        .eq('session_id', existingSessionId)
        .then(({ error }) => {
          if (error) {
            console.error('Error clearing old drafts:', error);
          } else {
            console.log('Cleared old drafts for fresh inquiry');
          }
        });
    }
  }, [isReturningToEdit]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
    });
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
    });
    
    return () => subscription.unsubscribe();
  }, []);

  // Fetch homepage settings
  const { data: homepageSettings } = useQuery({
    queryKey: ["site-settings", "homepage"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("id", "homepage")
        .single();

      if (error) return { show_team_section: true };
      return data.value as unknown as HomepageSettings;
    },
    staleTime: 1000 * 60 * 5,
  });

  return (
    <div className="min-h-screen bg-background">
      <Navbar isAuthenticated={isAuthenticated} />
      
      {/* Hero - Primary conversion point */}
      <HeroSection />
      
      {/* Services - Build desire */}
      <ServicesSection />
      
      {/* Why Choose Us - Handle objections */}
      <WhyChooseUsSection />
      
      {/* AI Section - Differentiation + conversion */}
      <AITasteBuilderSection />
      
      {/* Team Section - Conditionally Rendered */}
      {homepageSettings?.show_team_section !== false && <TeamSection />}
      
      {/* Contact Form - Final conversion */}
      <ContactSection />
      
      <Footer />
      
      {/* Floating CTA - Always accessible conversion */}
      <FloatingCTA />
    </div>
  );
};

export default Index;
